/**
 * Commercial terms. Single source of truth — the Plan page, the calculator,
 * the billing routes and the allowance meters all read from here.
 *
 * OPEN QUESTION, still unresolved from the demonstration and flagged in
 * PRODUCTION-BUILD.md 15.5: whether the founding-customer discount stacks on
 * top of the PCN discount. `FOUNDING_STACKS` below encodes the assumption the
 * demonstration's calculator made (it does stack, applied after). It must be
 * confirmed commercially before billing goes live — it is a live variable
 * here rather than a hard-coded multiplier precisely so that changing it is a
 * one-line decision.
 */

export const PLANS = {
  core: {
    id: 'core',
    name: 'Core',
    monthlyGbp: 79,
    annualGbp: 790,
    translationsPerPracticePerYear: 60,
    humanReviewedTranslationsPerPracticePerYear: 0,
    fundingRadar: false,
  },
  /*
   * `id` is 'complete' and stays that way: it is the stored `plan_id` on every
   * organisation row, so renaming it is a migration rather than a word change.
   * `name` is the only thing a customer ever sees, and "Complete" was doing
   * the opposite of its job — it made the plan most practices should buy sound
   * like the one that fixes an incomplete Core.
   */
  complete: {
    id: 'complete',
    name: 'Plus',
    monthlyGbp: 109,
    annualGbp: 1090,
    translationsPerPracticePerYear: 240,
    humanReviewedTranslationsPerPracticePerYear: 1,
    fundingRadar: true,
  },
} as const;

/**
 * What each plan includes, in the words a customer reads.
 *
 * Here rather than in the marketing page so that the price and what it buys
 * cannot drift apart: the same module already supplies the figures to the
 * Plan page, the calculator and the billing routes. A plan gaining a feature
 * is one edit, not two files that agree until someone forgets.
 */
export const PLAN_INCLUDES: Record<PlanId, string[]> = {
  core: [
    'Unlimited staff accounts',
    'Unlimited document uploads, under fair use',
    'Contract and specification analysis',
    'Ask InBrief, with a source citation on every answer',
    'Patient communication generation',
    'Practice-specific details applied automatically',
    `${PLANS.core.translationsPerPracticePerYear} translation units per practice each year`,
    'Standard support',
  ],
  complete: [
    `${PLANS.complete.translationsPerPracticePerYear} translation units per practice each year`,
    'One human-reviewed translation credit per practice each year',
    'Priority support',
  ],
};

/**
 * Funding Radar on its own, for a Core customer who wants it without moving
 * plan. Priced per organisation rather than per practice: the opportunities a
 * PCN sees are the same opportunities whichever practice is looking.
 *
 * NOT OFFERED, and not mentioned anywhere on the public site.
 *
 * The software is built and tested — drafting, the publish gate that refuses
 * to assert eligibility, withdrawal, staleness, the scoring. What does not
 * exist is the content: there is no machine-readable feed of primary care
 * funding, so the board is curated by a person from ICB board papers and
 * tender notices, and nobody is doing that weekly yet.
 *
 * It was briefly presented as "in development" and is now absent instead,
 * which is the right call: a roadmap entry is still a promise, it still gets
 * asked about in a sales call, and a capability advertised before it works is
 * the one thing that would make every other claim on the site suspect. It
 * comes back to the site when there is a board behind it.
 *
 * The price is kept here rather than deleted so that re-offering it is a
 * decision about timing, not a commercial decision taken again from nothing.
 * See the Funding Radar section of README.md for what restoring it involves.
 */
export const FUNDING_RADAR_ADDON = {
  monthlyGbp: 49,
  annualGbp: 490,
  per: 'organisation',
} as const;

/**
 * Paid pilots. Twelve weeks, because a shorter run does not cross enough of a
 * contract year to tell anyone anything.
 */
export const PILOTS = [
  { id: 'practice', label: 'One practice', weeks: 12, priceGbp: 500 },
  { id: 'pcn', label: 'A PCN, up to six practices', weeks: 12, priceGbp: 1_000 },
] as const;

export const PILOT_CREDIT_DAYS = 30;


export type PlanId = keyof typeof PLANS;

/** Discount by practice count. 7+ is priced individually — no list price applies. */
export const PCN_DISCOUNT: Record<number, number> = { 1: 0, 2: 10, 3: 15, 4: 20, 5: 30, 6: 30 };

export const CUSTOM_PRICING_THRESHOLD = 7;

export const FOUNDING_DISCOUNT_PCT = 20;
export const FOUNDING_STACKS = true;

/**
 * The founding-customer terms. Deliberately carries `limited: true` — the
 * offer is a fixed cohort, and a site that implies it is permanently available
 * is making a promise nobody agreed to.
 */
export const FOUNDING_OFFER = {
  discountPct: FOUNDING_DISCOUNT_PCT,
  limited: true,
  terms: [
    'Assisted onboarding included',
    'Annual commitment',
    'Structured product feedback',
    'Agreed case-study participation',
  ],
} as const;

export interface QuoteLine {
  label: string;
  amountGbp: number | null;
  note?: string;
}

export interface Quote {
  custom: boolean;
  lines: QuoteLine[];
  totalGbp: number | null;
  perPracticeGbp: number | null;
  perPracticeMonthlyGbp: number | null;
}

/** The published price list is quoted in whole pounds, so round to match it. */
const round = (v: number) => Math.round(v);

export function quote(planId: PlanId, practices: number, founding: boolean): Quote {
  const plan = PLANS[planId];

  if (practices >= CUSTOM_PRICING_THRESHOLD) {
    return {
      custom: true,
      lines: [
        {
          label: `${plan.name}, ${CUSTOM_PRICING_THRESHOLD} or more practices`,
          amountGbp: null,
          note: 'Seven or more practices are priced individually, so no list price applies.',
        },
      ],
      totalGbp: null,
      perPracticeGbp: null,
      perPracticeMonthlyGbp: null,
    };
  }

  const pct = PCN_DISCOUNT[practices] ?? 0;
  const list = plan.annualGbp * practices;
  const afterPcn = list * (1 - pct / 100);
  const total = founding && FOUNDING_STACKS ? afterPcn * (1 - FOUNDING_DISCOUNT_PCT / 100) : afterPcn;

  const lines: QuoteLine[] = [
    { label: `${plan.name} × ${practices} practice${practices > 1 ? 's' : ''}`, amountGbp: round(list) },
    { label: `PCN discount (${pct}%)`, amountGbp: pct ? -round(list - afterPcn) : 0 },
  ];
  if (founding && FOUNDING_STACKS) {
    lines.push({
      label: `Founding customer (${FOUNDING_DISCOUNT_PCT}%)`,
      amountGbp: -round(afterPcn - total),
    });
  }

  return {
    custom: false,
    lines,
    totalGbp: round(total),
    perPracticeGbp: round(total / practices),
    perPracticeMonthlyGbp: round(total / practices / 12),
  };
}

export function translationAllowance(planId: PlanId, practices: number): number {
  return PLANS[planId].translationsPerPracticePerYear * practices;
}

export const money = (v: number) =>
  '£' + Math.round(v).toLocaleString('en-GB');

/**
 * InBrief Services — human consultancy, priced separately. These are
 * deliberately NOT presented as required to use the software, and nothing in
 * the software generates the strategies these services provide.
 */
export const SERVICES = [
  {
    id: 'contract-assurance',
    name: 'Contract Assurance Review',
    rate: 'Quoted per engagement',
    summary: 'A read of a contract or specification by someone who has delivered one, against what your organisation actually does.',
  },
  {
    id: 'performance-strategy',
    name: 'Performance Improvement Strategy',
    rate: 'Quoted per engagement',
    summary: 'Where delivery is falling short of a specification, what to change and in what order.',
  },
  {
    id: 'mobilisation',
    name: 'Mobilisation Sprint',
    rate: 'Day rate, quoted per engagement',
    summary: 'Standing up a newly awarded service: rotas, pathways, recording, reporting.',
  },
  {
    id: 'kpi-setup',
    name: 'KPI and Reporting Setup',
    rate: 'Quoted per engagement',
    summary: 'Turning the indicators in a contract into searches, dashboards and a reporting rhythm.',
  },
  {
    id: 'comms-review',
    name: 'Patient Communication Review',
    rate: 'Per communication set, quoted',
    summary: 'A review of the letters and messages you send, for clarity, reading age and accuracy.',
  },
  {
    id: 'funding-application-review',
    name: 'Funding Application Review',
    rate: 'Per application, quoted',
    summary: 'A second pair of eyes on a bid before it goes in.',
  },
  {
    id: 'funding-bid-sprint',
    name: 'Funding Bid Sprint',
    rate: 'Quoted per engagement',
    summary: 'Writing a bid with you, to a deadline.',
  },
  {
    id: 'partnership-support',
    name: 'Partnership Support',
    rate: 'Quoted per engagement',
    summary: 'Working across practices, PCNs, federations and providers where an agreement spans several organisations.',
  },
  {
    id: 'health-check',
    name: 'Service Delivery Health Check',
    rate: 'Quoted per engagement',
    summary: 'A structured look at a running service and what is at risk in it.',
  },
  {
    id: 'consultation',
    name: 'Expert consultation',
    rate: 'Hourly or half-day',
    summary: 'A conversation with someone who knows the contract landscape, when that is all you need.',
  },
  {
    id: 'retainer',
    name: 'Advisory retainer',
    rate: 'Monthly, quoted',
    summary: 'Ongoing access to the same adviser, rather than a new briefing each time.',
  },
] as const;

/**
 * Said on the marketing site and on the Services page, in the same words.
 *
 * The line between software and consultancy is what keeps this product outside
 * the medical device regime, and a marketing page is exactly where that line
 * gets blurred by accident. It is stated once, here, and rendered in both.
 */
export const SERVICES_BOUNDARY =
  'These are optional services delivered by people. None of them is required to use the software, ' +
  'the software does not generate them, and buying one does not change what the software does.';
